<template>
  <v-text-field
    autocomplete="off"
    :background-color="backgroundColor"
    class="p-numeric-field"
    :class="[
      { 'p-numeric-field-right': textAlignRight },
      {
        'v-input--legacy-dirty':
          inputValue !== null && inputValue !== undefined && inputValue !== ''
      }
    ]"
    :clearable="clearable"
    :counter="counter"
    :dense="dense"
    :disabled="disabled"
    :error="error"
    :filled="filled"
    :hide-details="hideDetails"
    :label="label"
    :loading="loading"
    :outlined="outlined"
    :prefix="prefix"
    :required="required"
    :rules="rules"
    :suffix="suffix"
    :type="type"
    v-model="inputValue"
    @keydown="keydown"
    @input="input"
    @change="change"
    @focus="focus"
    @blur="blur"
  ></v-text-field>
</template>

<script>
import { mapGetters } from 'vuex'
import { formatNumberForLocale } from '@/utils/numberFormatting'

const WHITELISTED_KEYS = [
  8 /* Backspace */, 9 /* Tab */, 37 /* ArrowLeft */, 39 /* ArrowRight */,
  46 /* Delete */
]
const SPECIAL_CHARS_REGEX = /[`a-zA-Z!@#$%^&*()_+=[\]{};':"\\|,<>/?~ ]/

export default {
  name: 'i-numeric-field',

  props: {
    value: {
      type: [Number, String],
      default: ''
    },
    preventEmpty: {
      type: Boolean,
      default: false
    },
    emptyZero: {
      type: Boolean,
      default: false
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 999
    },
    precision: {
      type: Number,
      default: 0
    },
    minPrecision: {
      type: Number,
      default: NaN
    },
    textAlignRight: {
      type: Boolean,
      default: false
    },

    // Pass-through attributes to underlying v-text-field
    backgroundColor: {
      type: String,
      default: undefined
    },
    clearable: {
      type: Boolean,
      default: false
    },
    counter: {
      type: [Boolean, String, Number],
      default: undefined
    },
    dense: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    filled: {
      type: Boolean,
      default: false
    },
    hideDetails: {
      type: [Boolean, String],
      default: false
    },
    label: {
      type: String,
      default: undefined
    },
    loading: {
      type: Boolean,
      default: false
    },
    outlined: {
      type: Boolean,
      default: false
    },
    prefix: {
      type: String,
      default: undefined
    },
    required: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      default() {
        return []
      }
    },
    suffix: {
      type: String,
      default: undefined
    }
  },

  data() {
    return {
      inputValue: this.formatValueForDisplay(this.value),
      // inputEventEmitted is used so that when watching for changes to 'value' we can ignore those that were triggered
      // by this component!
      inputEventEmitted: false
    }
  },

  computed: {
    ...mapGetters('tenant', ['tenantLocale']),

    type() {
      return 'text'
      // Note: the below is currently disabled because when using 'number' type the keypress event doesn't seem to
      // be provided with selectionStart and selectionEnd information!
      // return this.precision === 0 ? 'number' : 'text'
    }
  },

  methods: {
    containsSpecialChars(value) {
      return SPECIAL_CHARS_REGEX.test(value)
    },

    isDeletionKey(e) {
      return e.keyCode === 46 || e.keyCode === 8
    },

    computeFutureInputValue(e) {
      const isTextSelected = e.target.selectionEnd !== e.target.selectionStart
      const isDelete = e.keyCode === 46
      const isBackspace = e.keyCode === 8
      // If the user is deleting a single character when text is NOT selected then we need to strip that character
      // from the computed future value! If text is selected during the deletion then it's already handled with
      // the splice logic below.
      const singleCharacterBackspaceOffset =
        isBackspace && !isTextSelected ? 1 : 0
      const singleCharacterDeleteOffset = isDelete && !isTextSelected ? 1 : 0
      return String(
        this.inputValue.slice(
          0,
          e.target.selectionStart - singleCharacterBackspaceOffset
        ) +
          (this.isDeletionKey(e) ? '' : e.key) +
          this.inputValue.slice(
            e.target.selectionEnd + singleCharacterDeleteOffset
          )
      )
    },

    // Formats a given value with the minimum number of digits based on the specified precision.  This is really
    // only used on initial display to format a raw number returned from an API, for example, into something that
    // looks like currency
    formatValueForDisplay(value) {
      if (value !== undefined && value !== null && value !== '') {
        if (this.emptyZero && Number(value) === 0) return ''
        const formatOptions = {
          useGrouping: false,
          minimumFractionDigits:
            this.minPrecision >= 0 ? this.minPrecision : this.precision,
          maximumFractionDigits: this.precision
        }
        return formatNumberForLocale(value, formatOptions, this.tenantLocale)
      }
      return ''
    },

    /**
     * The purpose of this method is to receive every keypress received by the numeric input text field and determine
     * those that should be prevented because they are:
     *  1. not valid inputs for a numeric field, or
     *  2. would result in exceeding a minimum, maximum, or precision length limit
     *
     * @param e The keypress event
     */
    keydown(e) {
      // Prevent paste from clipboard
      if (e.metaKey && e.key === 'v') {
        e.preventDefault()
        return
      }
      if (e.key === '.') {
        if (this.precision <= 0) {
          e.preventDefault()
          return
        } else if (String(this.inputValue).includes('.')) {
          e.preventDefault()
          return
        }
      }
      if (e.key === '-') {
        if (this.min >= 0) {
          e.preventDefault()
          return
        } else if (String(this.inputValue).includes('-')) {
          e.preventDefault()
          return
        }
      }

      // Prevents deletion of input causing the maximum to be exceeded (i.e. delete a decimal point!)
      if (this.isDeletionKey(e)) {
        const futureInputValue = this.computeFutureInputValue(e)
        if (this.preventEmpty && futureInputValue === '') {
          e.preventDefault()
          return
        } else if (futureInputValue === '') return
        if (
          Number(futureInputValue) > this.max ||
          Number(futureInputValue) < this.min
        ) {
          e.preventDefault()
        }
      } else if (
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !WHITELISTED_KEYS.includes(e.keyCode)
      ) {
        // If it's a non-numeric key then prevent the event from proceeding
        if (this.containsSpecialChars(e.key)) {
          e.preventDefault()
          return
        }

        const futureInputValue = this.computeFutureInputValue(e)
        // Limit exceeding the min and max limits
        if (
          Number(futureInputValue) > this.max ||
          Number(futureInputValue) < this.min
        ) {
          e.preventDefault()
          return
        }

        // Limit entering more trailing digits than the precision allows
        const futureInputValueString = futureInputValue
        if (futureInputValueString.includes('.')) {
          const leadingDigitsCount = futureInputValueString.indexOf('.')
          const trailingDigitsCount =
            futureInputValueString.length - 1 - leadingDigitsCount
          if (
            trailingDigitsCount > this.precision &&
            e.target.selectionStart > leadingDigitsCount
          )
            e.preventDefault()
        }
      }
    },

    input() {
      if (!isNaN(this.inputValue)) {
        // Emitted value is first converted to a number to ensure that any coercion of values done in keydown
        // are also reflected in the final value. It's then converted to a string for backwards compatibility as many
        // callees currently expect it to be and may perform string specific method calls.
        // HOWEVER, if the input value is empty AND we're not preventing empty then we don't want to convert the value to
        // a number because this could result in an empty string becoming 0!
        if (
          this.preventEmpty ||
          (this.inputValue !== '' && this.inputValue !== null)
        ) {
          if (this.inputValue[this.inputValue.length - 1] !== '.') {
            // Converting inputValue ending in '.' will result in it being stripped!
            // The below endeavours to retain any trailing 0's
            const precision =
              this.inputValue.indexOf('.') > -1
                ? this.inputValue.length - 1 - this.inputValue.indexOf('.')
                : 0
            this.inputValue = Number(this.inputValue).toFixed(precision)
          }
        }
        this.$emit('input', this.inputValue)
        this.inputEventEmitted = true
      }
    },

    change() {
      if (!isNaN(this.inputValue)) {
        if (this.inputValue === '') {
          this.$emit('change', '')
          return
        }
        // Emitted value is first converted to a number to ensure that any coercion of values done in keydown
        // are also reflected in the final value. It's then converted to a string for backwards compatibility as many
        // callees currently expect it to be and may perform string specific method calls.
        this.$emit('change', Number(this.inputValue).toString())
      }
    },

    focus() {
      if (this.emptyZero && Number(this.inputValue) === 0) {
        this.inputValue = ''
      }
    },

    blur() {
      this.inputValue = this.formatValueForDisplay(this.inputValue)
    }
  },

  mounted() {},

  watch: {
    value() {
      if (!this.inputEventEmitted)
        this.inputValue = this.formatValueForDisplay(this.value)
      this.inputEventEmitted = false
    }
  }
}
</script>
<style lang="scss">
.p-numeric-field {
  &-right {
    input {
      text-align: right;
    }
  }
}
</style>
<style lang="scss" scoped>
.p-numeric-field {
}
</style>
